﻿
export class GeminiAudioOutput {
    static outputAudioContext: AudioContext;
    static AudioElement: HTMLAudioElement | null = null;
    static outputGain: GainNode;
    static MediaStreamDestination: MediaStreamAudioDestinationNode;
    static sources = new Set<AudioBufferSourceNode>();
    static nextStartTime = 0;
    static Playing = false;
    
    static async Play(data: string, mime: string|undefined){
        
        try {
            if (!GeminiAudioOutput.Playing) {
                GeminiAudioOutput.Playing = true;
                
                GeminiAudioOutput.outputAudioContext = new ((window as any).AudioContext || (window as any).webkitAudioContext)({sampleRate: 24000});
                GeminiAudioOutput.outputGain = GeminiAudioOutput.outputAudioContext.createGain();
                GeminiAudioOutput.MediaStreamDestination = GeminiAudioOutput.outputAudioContext.createMediaStreamDestination();
                GeminiAudioOutput.outputGain.connect(GeminiAudioOutput.MediaStreamDestination);
                
                if (!GeminiAudioOutput.AudioElement) {
                    const el = new Audio();
                    el.autoplay = true;
                    el.srcObject = GeminiAudioOutput.MediaStreamDestination.stream;
                    el.play().catch(err => console.warn("Autoplay blocked, user interaction required", err));
                    GeminiAudioOutput.AudioElement = el;
                } else {
                    GeminiAudioOutput.AudioElement.srcObject = GeminiAudioOutput.MediaStreamDestination.stream;
                    GeminiAudioOutput.AudioElement.play().catch(err => console.warn("Autoplay blocked, user interaction required", err));
                }

                GeminiAudioOutput.nextStartTime = GeminiAudioOutput.outputAudioContext.currentTime;
            }

            const audioBuffer = await GeminiAudioOutput.DecodeAudioBuffer(data, mime, GeminiAudioOutput.outputAudioContext);
            
            GeminiAudioOutput.nextStartTime = Math.max(GeminiAudioOutput.nextStartTime, GeminiAudioOutput.outputAudioContext.currentTime);

            const src = GeminiAudioOutput.outputAudioContext.createBufferSource();
            src.buffer = audioBuffer;
            src.connect(GeminiAudioOutput.outputGain);
            src.addEventListener("ended", () => {
                GeminiAudioOutput.sources.delete(src);
            });

            src.start(GeminiAudioOutput.nextStartTime);
            GeminiAudioOutput.nextStartTime += audioBuffer.duration;
            GeminiAudioOutput.sources.add(src);
        }
        catch (e) {
            console.error("Error while playing audio", e);
        }
    }

    static Stop():void {
        if (!GeminiAudioOutput.Playing) return;
        GeminiAudioOutput.Playing = false;
        
        try {
            for (const s of GeminiAudioOutput.sources.values()) {
                try {
                    s.stop();
                } catch {
                    console.log("Failed to stop audio source");
                }
            }
            GeminiAudioOutput.sources.clear();
            GeminiAudioOutput.nextStartTime = 0;
            GeminiAudioOutput.outputGain?.disconnect();
            GeminiAudioOutput.outputAudioContext?.close();
        }
        catch (e) {
            console.error("Error while stopping audio output", e);
        }
    }

    static async DecodeAudioBuffer(
        data: string,
        mime: string | undefined,
        ctx: AudioContext,
    ): Promise<AudioBuffer> {
        
        if (!data) throw new Error("Missing inline audio data");
        const mimeType = (mime ?? "").toLowerCase();

        // If the server returns raw PCM, convert it to an AudioBuffer directly.
        // This avoids container decoding heuristics and reduces artefacts/crackling.
        if (mimeType.startsWith("audio/pcm") || mimeType.startsWith("audio/l16")) {
            const binary = atob(data);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

            // Ensure Int16 alignment.
            const byteLength = bytes.byteLength - (bytes.byteLength % 2);
            const pcmBytes = bytes.subarray(0, byteLength);
            const pcm16 = new Int16Array(pcmBytes.buffer, pcmBytes.byteOffset, pcmBytes.byteLength / 2);

            // Extract sample rate from mimeType; if absent, assume 24000 (common Gemini audio out).
            const rateMatch = mimeType.match(/rate=(\d+)/);
            const sourceRate = rateMatch ? parseInt(rateMatch[1], 10) : 24000;

            // Create AudioBuffer at the *source* sample rate, then let WebAudio resample when playing into ctx.
            const audioBuffer = new AudioBuffer({length: pcm16.length, numberOfChannels: 1, sampleRate: sourceRate});
            const ch0 = audioBuffer.getChannelData(0);
            for (let i = 0; i < pcm16.length; i++) ch0[i] = pcm16[i] / 32768;

            return audioBuffer;
        }

        // Default: assume it's a decodable container (wav/ogg/mp3/etc.)
        const binary = atob(data);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

        return await ctx.decodeAudioData(bytes.buffer.slice(0));
    }
}
